import React from 'react';
import useFetch from 'use-http';
import Moment from 'react-moment';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileDown, Eye, Download, SlashIcon } from 'lucide-react';
import { Heading } from '@/components/ui/shared/heading';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Link } from 'react-router-dom/cjs/react-router-dom';

const InstrumentalResearcheDetails = () => {
  const history = useHistory();
  const { get: getFile } = useFetch('/UserAccount/DownloadAmisFile');
  const research = history.location.state?.data;

  const handleFileDownload = async (filename, fileType, fileGuid) => {
    await getFile(
      `?fileType=${fileType}&fileGuid=${fileGuid}&category=InstrumentalResearch`,
    ).then((res) => {
      const base64Data = res;
      const blob = base64toBlob(base64Data, 'application/pdf');
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = `${filename}.pdf`;
      document.body.appendChild(downloadLink);
      setTimeout(() => {
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }, 120);
    });
  };

  const base64toBlob = (base64Data, contentType = '') => {
    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: contentType });
  };

  const formatDate = (dateStr) =>
    new Date(Date.parse(dateStr)).getUTCHours() === 0 ? (
      <Moment utc format="YYYY-MM-DD">
        {dateStr}
      </Moment>
    ) : (
      <Moment utc format="YYYY-MM-DD HH:mm">
        {dateStr}
      </Moment>
    );

  return (
    <div className="space-y-5">
      <Breadcrumb className="px-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/userAccount/instrumental-researches">Instrumentiniai tyrimai</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <SlashIcon />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>{research.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Card 1: Text + Observations */}
      <Card>
        <CardHeader>
          <Heading variant="userAccountDetailsTitle">{research.name}</Heading>
        </CardHeader>
        <CardContent className="space-y-2">
          <div>
            <span>Data: </span>
            <span>{formatDate(research.orderDate)}</span>
          </div>

          {research.statusDisplay && (
            <div>
              <span>Būsena: </span>
              <span>{research.statusDisplay}</span>
            </div>
          )}

          {research.statusDisplay !== 'Užbaigtas' &&
            research.statusDisplay !== 'Neatliktas' && (
              <div className="text-red-600 font-medium">LAUKIAMA REZULTATŲ</div>
            )}

          {/* Observations */}
          {research.observations?.length > 0 && (
            <>
              <div className="pt-4 font-semibold uppercase text-sm">
                Instrumentinių tyrimų vertinimai:
              </div>

              {research.observations.map((result, index) => (
                <div key={index} className="space-y-2 pt-2">
                  <div>
                    <span>Data: </span>
                    <span>{formatDate(result.date)}</span>
                  </div>
                  <div>
                    <span>Dokumento numeris: </span>
                    <span>{result.documentNumber}</span>
                  </div>
                  <div>
                    <span>Radiologo vardas: </span>
                    <span>{result.radiologist?.name}</span>
                  </div>
                  <div>
                    <span>Radiologo pavardė: </span>
                    <span>{result.radiologist?.surname}</span>
                  </div>
                  <div>
                    <span>Radiologo technologo vardas: </span>
                    <span>{result.radiologyTechnologist?.name}</span>
                  </div>
                  <div>
                    <span>Radiologo technologo pavardė: </span>
                    <span>{result.radiologyTechnologist?.surname}</span>
                  </div>

                  {result.value && (
                    <div>
                      <span>Išvados:</span>
                      <p>{result.value}</p>
                    </div>
                  )}
                  {result.description && (
                    <div>
                      <span>Aprašymas:</span>
                      <p>{result.description}</p>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </CardContent>
        <CardFooter />
      </Card>

      {/* Card 2: Dokumentai */}
      {research.observations?.[0]?.file?.guid && (
        <Card>
          <CardHeader>
            <Heading variant="userAccountDetailsTitle">Dokumentai</Heading>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-4">
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => {
                  const fileName = `Instrumentinis tyrimas ${moment(
                    research.observations[0].date,
                  ).format('YYYY-MM-DD')}`;
                  handleFileDownload(
                    fileName,
                    research.observations[0].file.type,
                    research.observations[0].file.guid,
                  );
                }}
              >
                <FileDown className="w-4 h-4" />
                Atsisiųsti tyrimo PDF
              </Button>

              <Button variant="outline" className="gap-2" disabled>
                <Eye className="w-4 h-4 text-yellow-500" />
                Peržiūrėti tyrimo vaizdus
              </Button>

              <Button variant="outline" className="gap-2" disabled>
                <Download className="w-4 h-4 text-blue-600" />
                Atsisiųsti tyrimo vaizdus
              </Button>
            </div>
          </CardContent>
          <CardFooter />
        </Card>
      )}
    </div>
  );
};

export default InstrumentalResearcheDetails;
